import {
  Box,
  Button,
  InputAdornment,
  Modal,
  Stack,
  Switch,
  TextEditor,
  TextField,
  Typography,
} from '@common/components'
import { ThemeProvider } from '@common/utils/styles'
import lightTheme from '@styles/themes/light-theme'
import React from 'react'
import {
  ClassroomTaskResponse,
  useClassroomUpdateClassroomTaskMutation,
} from '@app/services/auto-generated/classroom'
import parse from 'html-react-parser'
import { LinkIcon } from '@common/icons'

interface ModalDisplayNoteProps {
  open: boolean
  onClose: () => void
  task: ClassroomTaskResponse
  is_ai: boolean
}

const ModalDisplayNote: React.FC<ModalDisplayNoteProps> = (props) => {
  const { open, onClose, task, is_ai } = props
  const [text, setText] = React.useState('')
  const [updateTask] = useClassroomUpdateClassroomTaskMutation()
  const onUpdate = async (formData) => {
    try {
      await updateTask({
        classroomTaskUpdateRequest: {
          color: formData.color,
          external_link: formData.external_link,
          questions: formData.questions,
          video_link: formData.video_link,
        },
        taskId: task.id,
      }).unwrap()
    } catch (error) {}
  }

  React.useEffect(() => {
    if (task) {
      let taskQuestions = ''
      task.questions.forEach((question) => {
        taskQuestions = taskQuestions + question.text
      })
      setText(taskQuestions)
    }
  }, [task])

  return (
    <>
      {is_ai ? (
        <Modal
          open={open}
          showCloseBtn={false}
          paperProps={{
            sx: { width: '500px', height: '370px', color: 'black' },
          }}
        >
          <ThemeProvider theme={lightTheme}>
            <AiNoteCard note={task} noteContent={text} onCancel={onClose} />
          </ThemeProvider>
        </Modal>
      ) : (
        <Modal
          open={open}
          onClose={onClose}
          paperProps={{
            sx: { p: 2, width: '500px' },
          }}
        >
          <NoteEditor note={task} noteContent={text} onCancel={onClose} onUpdate={onUpdate} />
        </Modal>
      )}
    </>
  )
}

const NoteEditor = (props: any) => {
  const { note, noteContent, onCancel, onUpdate } = props
  const [color, setColor] = React.useState(note ? note.color : '#FFF0C8')
  const [videoLink, setVideoLink] = React.useState(note ? note?.video_link : '')
  const [externalLink, setExternalLink] = React.useState(note ? note?.external_link : '')
  const [questions, setQuestions] = React.useState(noteContent)

  React.useEffect(() => {
    setQuestions(noteContent.split(/\n(?=<)/))
  }, [noteContent])

  const handleChange = (n: string) => {
    setQuestions(n.split(/\n(?=<)/))
  }
  const handleColorChange = (c: string) => {
    setColor(c)
  }
  const handleChangeVideo = (event: React.ChangeEvent<HTMLInputElement>) => {
    setVideoLink(event.target.value)
  }

  const handleChangeExternalLink = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExternalLink(event.target.value)
  }

  const [checked, setChecked] = React.useState(videoLink ? true : false)
  const [checkedExternal, setCheckedExternal] = React.useState(externalLink ? true : false)
  const handleChangeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked)
    // setCheckedExternal(false)
  }

  const handleChangeSwitchExternal = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedExternal(event.target.checked)
    // setChecked(false)
  }

  return (
    <Stack>
      <Typography fontWeight={600} sx={{ pt: 0.3 }}>
        Speaking task
      </Typography>
      <Stack
        sx={{ mt: 1, pb: 1, height: '30px' }}
        direction='row'
        spacing={2}
        alignItems={'center'}
        width={'100%'}
      >
        <Typography sx={{ width: '110px' }}>Include video</Typography>
        <Switch onChange={handleChangeSwitch} checked={checked} />
        {checked && (
          <TextField
            placeholder='Add YouTube link '
            value={videoLink}
            onChange={handleChangeVideo}
            variant='standard'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <LinkIcon sx={{ transform: 'rotate(-45deg)' }} />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>
      <Stack
        sx={{ my: 1, pb: 1, height: '30px' }}
        direction='row'
        spacing={2}
        alignItems={'center'}
        width={'100%'}
      >
        <Typography sx={{ width: '110px' }}>External link</Typography>
        <Switch onChange={handleChangeSwitchExternal} checked={checkedExternal} />
        {checkedExternal && (
          <TextField
            placeholder='Add External link '
            value={externalLink}
            onChange={handleChangeExternalLink}
            variant='standard'
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <LinkIcon sx={{ transform: 'rotate(-45deg)' }} />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>
      <TextEditor
        bgPickOption
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
        htmlContent={noteContent}
        color={color}
        onColorChange={handleColorChange}
        onChange={handleChange}
      />
      <Stack direction={'row'} alignItems='center' spacing={2} mt={1}>
        <Button
          onClick={onCancel}
          variant='outlined'
          color='neutral'
          sx={{ flex: 1, borderRadius: '5px' }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => {
            onCancel()
            if (noteContent !== '' && noteContent !== '<p></p>\n' && noteContent !== '<p></p>') {
              onUpdate({
                color: color,
                questions: questions,
                video_link: videoLink,
                external_link: externalLink,
              })
            }
          }}
          variant='contained'
          sx={{ flex: 1, borderRadius: '5px' }}
        >
          Save
        </Button>
      </Stack>
    </Stack>
  )
}

const AiNoteCard = (props: any) => {
  const { note, noteContent, onCancel } = props
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        borderRadius: '4px',
        overflow: 'hidden',
        backgroundColor: note.color,
      }}
    >
      <Button
        onClick={() => onCancel()}
        color='error'
        key={'3'}
        variant='outlined'
        size='small'
        sx={{
          border: '1px solid rgba(0,0,0,0.1)',
          ml: 1,
          height: '31.6px',
          borderRadius: '0px',
          width: '40px',
          backgroundColor: 'white',
          position: 'relative',
          left: '80%',
          top: '5%',
        }}
      >
        cancel
      </Button>
      <Typography sx={{ mt: 4, ml: 2, mr: 2 }}>{parse(noteContent)}</Typography>
    </Box>
  )
}

export default ModalDisplayNote
